import { useMemo } from "react";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export function OnboardingChecklist({ pool, memberships, connectReady, onConnectStripe, onInvite, onLinkBank, onCreateInvoice }) {
    const tasks = useMemo(() => {
        const me = (pool?.members || []).find((m) => m.is_owner);
        const hasBank = !!me?.bank?.account_last4;
        const hasOtherAdmin = (memberships?.length || 0) > 1;
        const hasRevenue = (pool?.total_revenue || 0) > 0;
        const recipientsCount = (pool?.members || []).filter((m) => (m.status ?? "active") === "active").length;
        return [
            { id: "connect", title: "Connect Stripe", desc: "Client payments deposit to your Stripe account.", done: !!connectReady, action: onConnectStripe },
            { id: "bank", title: "Link your bank account", desc: "For payout records and member bank details.", done: hasBank, action: () => onLinkBank?.(me) },
            { id: "invite", title: "Invite a teammate", desc: "Add an Admin so high-impact actions go through approval.", done: hasOtherAdmin, action: onInvite },
            { id: "recipients", title: "Set up recipients", desc: "You currently have " + recipientsCount + " active recipients.", done: recipientsCount >= 2 },
            { id: "stripe", title: "Bill your first client", desc: "Send a checkout link — revenue auto-funds this pool.", done: hasRevenue, action: onCreateInvoice },
        ];
    }, [pool, memberships, connectReady, onConnectStripe, onInvite, onLinkBank, onCreateInvoice]);

    const done = tasks.filter((t) => t.done).length;
    const complete = done === tasks.length;

    return (
        <section
            className={`surface p-5 sm:p-6 ${complete ? "pointer-events-none invisible max-h-0 overflow-hidden border-0 p-0 m-0" : ""}`}
            aria-hidden={complete}
            data-testid="onboarding-checklist"
        >
            <div className="flex items-end justify-between mb-4">
                <div>
                    <p className="label-eyebrow">Getting started</p>
                    <h3 className="font-heading text-xl tracking-tight mt-1">Set up your workspace</h3>
                </div>
                <div className="text-xs text-white/45 font-mono">{done}/{tasks.length}</div>
            </div>
            <div className="h-1 w-full rounded-full bg-white/[0.06] mb-5">
                <div className="h-full rounded-full bg-[#00BF0D] transition-all duration-500" style={{ width: `${(done / tasks.length) * 100}%` }} />
            </div>
            <ul className="space-y-2">
                {tasks.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 rounded-lg border border-white/[0.05] bg-white/[0.02] px-3.5 py-2.5" data-testid={`onboarding-task-${t.id}`}>
                        {t.done ? <CheckCircle2 className="h-4 w-4 text-[#00BF0D] shrink-0" /> : <Circle className="h-4 w-4 text-white/30 shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${t.done ? "text-white/55 line-through" : "text-white"}`}>{t.title}</p>
                            <p className="text-[11px] text-white/40">{t.desc}</p>
                        </div>
                        {!t.done && t.action && (
                            <button onClick={t.action} className="btn-ghost rounded-md px-2.5 py-1 text-xs inline-flex items-center gap-1" data-testid={`onboarding-action-${t.id}`}>
                                Set up <ArrowRight className="h-3 w-3" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>
            <p className="text-[11px] text-white/35 mt-4">
                <Link to="/settings" className="hover:text-white transition-colors">Manage workspace settings →</Link>
            </p>
        </section>
    );
}
